import { useState } from "react";
import { PanelShell, StatChip, TextField, DJSEMApiClient, tokens } from "../index";
import { DJSEMPlaylistIntentParser } from "@djsem/analysis-contract";

/**
 * Playlists module — a natural-language playlist builder: parses a free-text prompt locally
 * with the canonical intent parser (prompt 111) into target/duration/energy-arc/BPM fields,
 * plus a publishing-hub info panel.
 *
 * @param client - Accepted for interface consistency with other modules; unused here, since
 *   the intent parser runs entirely client-side.
 * @returns A playlist-prompt panel with parsed intent stat chips, and a publishing-hub info panel.
 */
export function PlaylistsModule({ client: _client }: { client: DJSEMApiClient }) {
  const [prompt, setPrompt] = useState("90 minute warm-up set, deep and melodic, no explicit tracks");
  const parser = useState(() => new DJSEMPlaylistIntentParser())[0];
  const req = parser.parse({ prompt });
  return (
    <>
      <PanelShell title="Playlist builder — describe what you need">
        <TextField
          value={prompt} onChange={setPrompt}
          placeholder="e.g. 60 min peak-time techno, high energy, end on a singalong"
          style={{ width: "100%", boxSizing: "border-box", padding: "9px 12px", fontSize: 13 }}
        />
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 12 }}>
          <StatChip label="Target" value={req.target ?? "any"} />
          <StatChip label="Duration" value={req.desiredDurationMinutes ? `${req.desiredDurationMinutes}m` : "—"} />
          <StatChip label="Energy arc" value={req.energyArc ?? "—"} tone="accent" />
          <StatChip label="Explicit" value={req.explicitPolicy ?? "allow"} />
        </div>
        {(req.bpmMin || req.bpmMax) ? (
          <div style={{ fontSize: 12, color: tokens.color.textDim, marginTop: 10 }}>
            BPM window: {req.bpmMin ?? "…"} – {req.bpmMax ?? "…"}
          </div>
        ) : null}
        <div style={{ fontSize: 11, color: tokens.color.muted, marginTop: 10 }}>
          Parsed locally by the canonical intent parser. Plan + readiness run via <code>/api/value-add/playlist/*</code>.
        </div>
      </PanelShell>
      <PanelShell title="Publishing hub" style={{ marginTop: 12 }}>
        <p style={{ margin: 0, color: tokens.color.textDim, fontSize: 13 }}>
          Dry-run first, then confirm. Spotify + SoundCloud capability-driven; execute is blocked
          until a verified write adapter is bound — no fake publishes.
        </p>
      </PanelShell>
    </>
  );
}
